import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import { toastSuccess } from 'utils/toast'
import {
  deleteWhitelistSegment,
  getWhitelistSegmentList,
  getWhitelistOtpDummyList,
  postCreateWhitelistSegment,
  putUpdateWhitelistSegment,
} from 'features/Segmentation/services/whitelistSegment'
import { TargetedUserType } from 'features/CategoryAndPosisi/@types/InfiniteScrollTabAddEdit'
import {
  WhitelistSegmentType,
  WhitelistFormPayloadType,
} from 'features/Segmentation/@types/WhitelistSegment'
import {
  SLICE_NAME,
  setIsLoading,
  setModal,
  setQuery,
  setDeleteTarget,
  setForms,
  resetForms,
  setWhitelistSegments,
  removeWhitelistSegmentById,
} from './slice'

export const fetchWhitelistSegmentList = createAsyncThunk(
  `${SLICE_NAME}/fetchWhitelistSegmentList`,
  async (_, { dispatch, getState, rejectWithValue }) => {
    dispatch(setIsLoading(true))

    try {
      const {
        whitelistSegment: { query },
      } = getState() as StoreStateType

      const { data } = await getWhitelistSegmentList({
        params: {
          limit: query.page_size,
          cursor: query.cursor || undefined,
          direction: query.direction,
          segment_name: query.search || undefined,
        },
      })

      dispatch(
        setWhitelistSegments(
          data.data.map<WhitelistSegmentType>((item) => ({
            customer_id: item.customer_id,
            phone_number: item.phone,
            user_type: item.user_type,
            active_segments: item.segments.map((segment) => ({
              id: segment.segment_id,
              name: segment.segment_name,
            })),
            created_date: item.created_at,
            last_modified: item.updated_at,
          })),
        ),
      )

      dispatch(
        setQuery({
          next_cursor: data.pagination.next_cursor,
          previous_cursor: data.pagination.previous_cursor,
          next_page_exist: data.pagination.next_page_exist,
          previous_page_exist: data.pagination.previous_page_exist,
        }),
      )

      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export const fetchWhitelistUserList = createAsyncThunk(
  `${SLICE_NAME}/fetchWhitelistUserList`,
  async ({ phoneNumber = '' }: { phoneNumber?: string }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getWhitelistOtpDummyList({ phone_number: phoneNumber })

      const targetedUsers = (data.data.customer_whitelist_otps ?? []).map<TargetedUserType>(
        (item) => ({
          customer_id: item.customer_id,
          customer_phone_number: item.phone_number,
          customer_name: item.name,
        }),
      )

      dispatch(setForms({ targetedUsers }))

      return targetedUsers
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    }
  },
)

export const createWhitelistSegment = createAsyncThunk(
  `${SLICE_NAME}/createWhitelistSegment`,
  async (payload: WhitelistFormPayloadType, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))

    try {
      const { data } = await postCreateWhitelistSegment({
        customer_id: payload.customerId,
        segments: payload.segments.map((segment) => ({
          segment_id: segment.id,
          segment_name: segment.name,
        })),
        user_type: 'EXISTING_USER',
        phone: payload.phoneNumber ?? '',
      })

      toastSuccess(data.data.message)
      dispatch(resetForms())
      dispatch(setModal({ mode: '' }))
      dispatch(fetchWhitelistSegmentList())

      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export const updateWhitelistSegment = createAsyncThunk(
  `${SLICE_NAME}/updateWhitelistSegment`,
  async (payload: WhitelistFormPayloadType, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))

    try {
      const { data } = await putUpdateWhitelistSegment({
        customerId: payload.customerId,
        segments: payload.segments.map((segment) => ({
          segment_id: segment.id,
          segment_name: segment.name,
        })),
        user_type: 'EXISTING_USER',
        phone: payload.phoneNumber ?? '',
      })

      toastSuccess(data.data.message)
      dispatch(resetForms())
      dispatch(setModal({ mode: '' }))
      dispatch(fetchWhitelistSegmentList())

      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)

export const removeWhitelistSegment = createAsyncThunk(
  `${SLICE_NAME}/removeWhitelistSegment`,
  async (target: WhitelistSegmentType, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))

    try {
      const { data } = await deleteWhitelistSegment({ customerId: target.customer_id })

      toastSuccess(data.data.message)
      dispatch(removeWhitelistSegmentById(target.customer_id))
      dispatch(setModal({ mode: '' }))
      dispatch(setDeleteTarget(null))

      return data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    } finally {
      dispatch(setIsLoading(false))
    }
  },
)
